/**
 * HP bar color logic for the Future Cartridge battle UI (PR 3).
 *
 * Two distinct signals share the bar:
 *   - **Threshold (state) color** — the FILL: green > 50 %, yellow
 *     25–50 %, red < 25 %. Cartridge convention; instantly legible
 *     regardless of which Pokémon is active.
 *   - **Type tint (identity) color** — the TRACK BACKGROUND: a faint
 *     wash of the active Pokémon's primary type. Reads as "this bar
 *     belongs to a Charmander" without competing with the threshold
 *     fill for attention.
 *
 * The two collide on warm/red-shifted types (Fire over a red threshold
 * = single warm gradient, kills the "critical?" read). Drop tint alpha
 * from 0.15 → 0.08 in the collision cases to preserve the threshold as
 * the dominant signal. Per design doc rev 6 "HP bar mechanism".
 *
 * Pure logic — testable in vitest Node. The Phaser-wired shell that
 * paints these onto a DOM element lives in PR 3's behavioral commits.
 */

export type Threshold = 'green' | 'yellow' | 'red';

/** Pokémon types that share the warm/red hue family. Fire is the
 *  obvious one; psychic and dragon are red-shifted in the Future
 *  Cartridge palette (#FF5C82, #7B5BFA — both lean magenta-toward-red).
 *  Fighting + ground complete the warm group. */
const TYPES_RED_COLLISION = new Set(['fire', 'fighting', 'dragon', 'psychic', 'ground']);

/** Yellow-shifted types — electric (canonical yellow), rock (warm
 *  amber), steel (light gray-yellow) all blend into the warn-amber
 *  threshold fill. */
const TYPES_YELLOW_COLLISION = new Set(['electric', 'rock', 'steel']);

/** Green-shifted types — grass (canonical), bug (yellow-green), poison
 *  (purple-green hybrid) all blend with the ok-green threshold fill. */
const TYPES_GREEN_COLLISION = new Set(['grass', 'bug', 'poison']);

/** Default tint alpha when type and threshold don't share a hue family. */
export const TINT_ALPHA_DEFAULT = 0.15;

/** Reduced tint alpha when type and threshold share a hue family. */
export const TINT_ALPHA_COLLISION = 0.08;

/** HP fraction below which the bar enters the "critical" sub-state
 *  (pulse + last-alive cinematic eligibility). Sub-state of red — the
 *  threshold is still red, just additionally critical. */
export const CRITICAL_PCT = 0.10;

const RED_PCT = 0.25;
const YELLOW_PCT = 0.50;

/** Resolve a HP fraction (0–1) to its threshold bucket. Boundaries
 *  are half-open ascending: hpPct === 0.5 returns green, hpPct === 0.25
 *  returns yellow. Matches the legacy Phaser BattleScene's tweenHpBar
 *  thresholds for cross-surface parity. */
export function thresholdFor(hpPct: number): Threshold {
  if (hpPct < RED_PCT) return 'red';
  if (hpPct < YELLOW_PCT) return 'yellow';
  return 'green';
}

/** True if HP is in the critical sub-state (< 10 %). The bar's primary
 *  threshold is still 'red' here; this flag drives the additional
 *  pulse + last-alive cinematic. */
export function isCritical(hpPct: number): boolean {
  return hpPct < CRITICAL_PCT;
}

/** Tint alpha to apply to the type-tinted track background, given the
 *  active Pokémon's primary type (canonical lowercase: 'fire', 'water',
 *  'grass', etc.) and the current HP threshold. Returns 0.08 on hue
 *  family collisions, 0.15 otherwise. Unknown / undeclared types fall
 *  through to the default — collision matrices are explicit allowlists. */
export function tintAlpha(type: string, threshold: Threshold): number {
  const lowered = type.toLowerCase();
  if (threshold === 'red' && TYPES_RED_COLLISION.has(lowered)) return TINT_ALPHA_COLLISION;
  if (threshold === 'yellow' && TYPES_YELLOW_COLLISION.has(lowered)) return TINT_ALPHA_COLLISION;
  if (threshold === 'green' && TYPES_GREEN_COLLISION.has(lowered)) return TINT_ALPHA_COLLISION;
  return TINT_ALPHA_DEFAULT;
}
